///
/// EarthBrowser
///
/// Created by Matt Giger on 12/13/2024
/// Copyright (c) 2024 Geodata Labs
///

import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import { useAppDispatch } from "@/redux/store"
import { setProfileOpen } from "@/redux/dialogSlice"
import { useGetPrivacySettingsQuery, useUpdatePrivacySettingsMutation } from "@/redux/userApi"
import { Loader2 } from "lucide-react"
import Link from "@/components/utils/Link"
import { useNavigate } from "react-router-dom"
import { updatePerformanceConsent } from "@/utils/privacy-consent"

export function PrivacySection() {
	const navigate = useNavigate()
	const dispatch = useAppDispatch()
	const { data: privacySettings, isLoading } = useGetPrivacySettingsQuery()
	const [updatePrivacySettings] = useUpdatePrivacySettingsMutation()

	const handleSwitchChange = async (id: string, checked: boolean) => {
		// Update tracking consent for performance cookies (PostHog and Sentry)
		if (id === "performance") {
			updatePerformanceConsent(checked)
		}

		// Update database (this will also sync to Redux via the onQueryStarted handler)
		try {
			await updatePrivacySettings({
				performance_cookies: id === "performance" ? checked : (privacySettings?.performance_cookies ?? true),
				personalization_cookies: id === "personalization" ? checked : (privacySettings?.personalization_cookies ?? true),
			}).unwrap()
		} catch (error) {
			console.error("Failed to update privacy settings:", error)
		}
	}

	const handleClick = (e: React.MouseEvent) => {
		e.preventDefault()
		dispatch(setProfileOpen(false))
		void navigate(`/privacy`)
	}

	// Show loading state while fetching privacy settings
	if (isLoading) {
		return (
			<div className="flex items-center justify-center py-4">
				<Loader2 className="h-5 w-5 animate-spin text-purple-500" />
			</div>
		)
	}

	return (
		<div className="space-y-3">
			{/* Cookie Settings */}
			<div className="space-y-2">
				<div className="rounded-lg border border-border bg-muted/30 p-3">
					<div className="flex items-center justify-between gap-3">
						<div className="flex-1">
							<Label htmlFor="necessary" className="flex flex-col gap-0.5 cursor-not-allowed">
								<span className="text-sm font-semibold">Strictly Necessary</span>
								<span className="text-xs text-muted-foreground font-normal leading-snug">
									Essential cookies for the website to function properly
								</span>
							</Label>
						</div>
						<Switch
							id="necessary"
							disabled
							checked={true}
						/>
					</div>
				</div>

				<div className="rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/50">
					<div className="flex items-center justify-between gap-3">
						<div className="flex-1">
							<Label htmlFor="performance" className="flex flex-col gap-0.5 cursor-pointer">
								<span className="text-sm font-semibold">Analytics & Error Reporting</span>
								<span className="text-xs text-muted-foreground font-normal leading-snug">
									Help us improve by sharing anonymous usage data and error reports
								</span>
							</Label>
						</div>
						<Switch
							id="performance"
							checked={privacySettings?.performance_cookies ?? true}
							onCheckedChange={(checked) => handleSwitchChange("performance", checked)}
						/>
					</div>
				</div>

				<div className="rounded-lg border border-border bg-background p-3 transition-colors hover:bg-muted/50">
					<div className="flex items-center justify-between gap-3">
						<div className="flex-1">
							<Label htmlFor="personalization" className="flex flex-col gap-0.5 cursor-pointer">
								<span className="text-sm font-semibold">Personalization</span>
								<span className="text-xs text-muted-foreground font-normal leading-snug">
									Remember your preferences and customize your experience
								</span>
							</Label>
						</div>
						<Switch
							id="personalization"
							checked={privacySettings?.personalization_cookies ?? true}
							onCheckedChange={(checked) => handleSwitchChange("personalization", checked)}
						/>
					</div>
				</div>
			</div>

			{/* Privacy Policy Link */}
			<div className="rounded-lg border border-dashed border-border p-3">
				<Link
					href="/privacy"
					onClick={handleClick}
					className="block space-y-1 group"
				>
					<span className="text-sm font-semibold text-primary group-hover:underline underline-offset-2">
						View Privacy Policy
					</span>
					<p className="text-xs text-muted-foreground leading-snug">
						Learn how we protect your personal information and use your data responsibly
					</p>
				</Link>
			</div>
		</div>
	)
}